import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, History, Upload, MailCheck } from "lucide-react";
import { api, errMsg } from "../lib/api";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtName = (n) => {
  const m = n.match(/backup_(\d{4}-\d{2}-\d{2})_(\d{2})(\d{2})(\d{2})?/);
  const up = n.includes("_upload") ? " · diunggah" : "";
  return m ? `${m[1]} · ${m[2]}.${m[3]}${m[4] ? `.${m[4]}` : ""} WIB${up}` : n;
};

function RestoreConfirm({ name, onDone, onCancel }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/backups/${name}/restore`);
      toast.success(`Data dipulihkan dari ${fmtName(name)}. Cadangan sebelum pemulihan: ${r.data.safetyBackup}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="restore-confirm" className="mt-2 rounded-xl border border-brand bg-brand-soft p-3 text-sm">
      <p className="font-semibold">Semua data sekarang (anggota, laporan, blocker, cuti, pengaturan) akan diganti dengan isi backup <b>{fmtName(name)}</b>.</p>
      <p className="mt-1 text-xs text-[#7a6667]">Data sekarang otomatis dibackup dulu, jadi bisa dikembalikan. PIN panel tidak berubah. Ketik <b>PULIHKAN</b> untuk lanjut.</p>
      <input data-testid="restore-confirm-input" className="field mt-2" value={text} onChange={(e) => setText(e.target.value)} placeholder="PULIHKAN" />
      <div className="mt-2 flex gap-2">
        <button data-testid="restore-confirm-button" disabled={text !== "PULIHKAN" || busy} onClick={go} className="btn-primary flex-1">{busy ? "Memulihkan…" : "Pulihkan sekarang"}</button>
        <button data-testid="restore-cancel-button" onClick={onCancel} className="btn-ghost">Batal</button>
      </div>
    </div>
  );
}

export default function PanelBackups({ version, onRestored }) {
  const [files, setFiles] = useState([]);
  const [confirm, setConfirm] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mailing, setMailing] = useState(false);
  const mailBackup = async () => {
    setMailing(true);
    try {
      const r = await api.post("/backups/email");
      toast.success(`Backup ${r.data.file} dikirim ke ${r.data.to}${r.data.attached ? "" : " (tanpa lampiran, file terlalu besar)"}`);
      load();
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setMailing(false);
    }
  };
  const fileRef = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backups/upload", fd);
      toast.success(`File diunggah (${r.data.counts.members} anggota, ${r.data.counts.reports} laporan). Tekan Pulihkan untuk memakainya.`);
      await load();
      setConfirm(r.data.file);
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setUploading(false);
    }
  };
  const load = useCallback(() => { api.get("/backups").then((r) => setFiles(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, version]);
  const download = async (name) => {
    try {
      const res = await api.get(`/backups/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-5 border-t border-brand-line pt-4" data-testid="backup-list">
      <span className="label">File backup (8 terakhir)</span>
      <input ref={fileRef} data-testid="backup-upload-input" type="file" accept=".json,application/json" className="hidden" onChange={upload} />
      <div className="mb-3 flex flex-wrap gap-2">
        <button data-testid="backup-upload-button" disabled={uploading} onClick={() => fileRef.current?.click()} className="btn-ghost">
          <Upload size={15} /> {uploading ? "Mengunggah…" : "Unggah file backup dari komputer"}
        </button>
        <button data-testid="backup-email-button" disabled={mailing} onClick={mailBackup} className="btn-ghost">
          <MailCheck size={15} /> {mailing ? "Mengirim…" : "Kirim backup terbaru ke email admin"}
        </button>
      </div>
      <p className="mb-3 text-xs text-[#7a6667]">Backup otomatis tiap Minggu 02.00 WIB juga dikirim sebagai lampiran ke email admin.</p>
      {files.length === 0 && <p data-testid="backup-empty" className="text-sm text-[#7a6667]">Belum ada backup. Tekan "Backup sekarang" atau tunggu jadwal Minggu 02.00.</p>}
      <div className="divide-y divide-brand-line rounded-xl border border-brand-line empty:hidden">
        {files.map((f) => (
          <div key={f.name} data-testid={`backup-item-${f.name}`} className="p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold">{fmtName(f.name)}</div>
                <div className="text-xs text-[#7a6667]">{fmtSize(f.size)} · JSON · {f.cloud ? <span data-testid={`backup-cloud-${f.name}`} className="font-bold text-emerald-700">tersimpan di cloud</span> : <span className="font-bold text-amber-700">hanya di server</span>}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`backup-download-${f.name}`} onClick={() => download(f.name)} className="btn-ghost shrink-0 px-3"><Download size={15} /> Unduh</button>
                <button data-testid={`backup-restore-${f.name}`} onClick={() => setConfirm(f.name)} className="btn-soft shrink-0 px-3"><History size={15} /> Pulihkan</button>
              </div>
            </div>
            {confirm === f.name && (
              <RestoreConfirm name={f.name} onCancel={() => setConfirm("")} onDone={() => { setConfirm(""); load(); onRestored?.(); }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
